import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const DEFAULT_TITLE = 'Quanthop — Research, Optimize & Validate Trading Strategies';
const DEFAULT_DESCRIPTION =
  'A professional research environment for systematic traders. Backtest, run multi-asset optimization, and continuously validate strategies with Quanthop.';
const SITE_ORIGIN = 'https://quanthop.com';

interface PageMeta {
  title: string;
  description?: string;
  noindex?: boolean;
}

export function usePageMeta({ title, description, noindex }: PageMeta) {
  const { pathname } = useLocation();

  useEffect(() => {
    const fullTitle = title === DEFAULT_TITLE ? title : `${title} | Quanthop`;
    document.title = fullTitle;

    // Canonical URL — always matches current pathname
    const canonicalUrl = `${SITE_ORIGIN}${pathname === '/' ? '/' : pathname.replace(/\/+$/, '')}`;
    const canonicalLink = document.getElementById('canonical-link') as HTMLLinkElement | null;
    if (canonicalLink) {
      canonicalLink.href = canonicalUrl;
    }

    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc && description) {
      metaDesc.setAttribute('content', description);
    }

    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) ogUrl.setAttribute('content', canonicalUrl);

    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) ogTitle.setAttribute('content', fullTitle);

    const ogDesc = document.querySelector('meta[property="og:description"]');
    if (ogDesc && description) ogDesc.setAttribute('content', description);

    const twTitle = document.querySelector('meta[name="twitter:title"]');
    if (twTitle) twTitle.setAttribute('content', fullTitle);

    const twDesc = document.querySelector('meta[name="twitter:description"]');
    if (twDesc && description) twDesc.setAttribute('content', description);

    // Handle noindex for auth pages
    let robotsMeta = document.querySelector('meta[name="robots"]') as HTMLMetaElement | null;
    if (noindex) {
      if (!robotsMeta) {
        robotsMeta = document.createElement('meta');
        robotsMeta.name = 'robots';
        document.head.appendChild(robotsMeta);
      }
      robotsMeta.content = 'noindex, nofollow';
    } else if (robotsMeta) {
      robotsMeta.remove();
    }

    return () => {
      document.title = DEFAULT_TITLE;
      if (canonicalLink) canonicalLink.href = `${SITE_ORIGIN}/`;
      if (metaDesc) metaDesc.setAttribute('content', DEFAULT_DESCRIPTION);
      if (ogUrl) ogUrl.setAttribute('content', `${SITE_ORIGIN}/`);
      if (ogTitle) ogTitle.setAttribute('content', DEFAULT_TITLE);
      if (ogDesc) ogDesc.setAttribute('content', DEFAULT_DESCRIPTION);
      if (twTitle) twTitle.setAttribute('content', DEFAULT_TITLE);
      if (twDesc) twDesc.setAttribute('content', DEFAULT_DESCRIPTION);
      if (robotsMeta && noindex) robotsMeta.remove();
    };
  }, [title, description, noindex, pathname]);
}
